import { useAuth } from "../context/AuthContext";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, RefreshCw, X, Rows3, Grid3x3, CalendarOff } from "lucide-react";
import { toLocalISODate } from "../utils/date";
import { openInvoicePreview } from "../utils/invoice";
import CalendarListView from "../components/admin/CalendarListView";
import BlockDatesModal from "../components/admin/BlockDatesModal";
import DayInventorySheet from "../components/admin/DayInventorySheet";
import DayRatesSheet from "../components/admin/DayRatesSheet";
import useRangeSelect from "../utils/useRangeSelect";
import { bookingLabel } from "../utils/bookingLabel";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/* `brand` is each channel's own colour and `mark` its shorthand, so a booking
   is identifiable at a glance by where it came from.
   
   Deliberately marks, not reproductions of the OTAs' logos: at the 10-16px a
   calendar chip allows, a real logo is an unreadable smudge, and drawing
   trademarked artwork approximately is worse than not drawing it. If you want
   the genuine logos, drop official SVGs into frontend/public/ota/ and this is
   the one place that has to change. */
export const SOURCE_CONFIG = {
  Airbnb:        { color: "bg-rose-400",   light: "bg-rose-50",   text: "text-rose-700",   border: "border-rose-200",   label: "Airbnb",      brand: "#FF5A5F", mark: "A" },
  "Booking.com": { color: "bg-blue-400",   light: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200",   label: "Booking.com", brand: "#003580", mark: "B." },
  Agoda:         { color: "bg-red-400",    light: "bg-red-50",    text: "text-red-700",    border: "border-red-200",    label: "Agoda",       brand: "#E4322B", mark: "ag" },
  MakeMyTrip:    { color: "bg-indigo-400", light: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200", label: "MakeMyTrip",  brand: "#EB2226", mark: "MMT" },
  Expedia:       { color: "bg-amber-400",  light: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-200",  label: "Expedia",     brand: "#191E3B", mark: "Ex" },
  "Hotels.com":  { color: "bg-orange-400", light: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", label: "Hotels.com",  brand: "#D32F2F", mark: "H" },
  Website:       { color: "bg-green-400",  light: "bg-green-50",  text: "text-green-700",  border: "border-green-200",  label: "Direct",      brand: "#16A34A", mark: "D" },
  Manual:        { color: "bg-purple-400", light: "bg-purple-50", text: "text-purple-700", border: "border-purple-200", label: "Manual",      brand: "#7C3AED", mark: "M" },
};

/* The channel manager names each OTA its own way ("AirBNB", "BookingCom",
   "MMT"), not the way the tags above are keyed. Mapped here so a booking wears
   its OTA's tag however it arrived — the dashboard maps the same spellings —
   instead of an anonymous purple chip beside the real one. */
const SOURCE_ALIASES = { airbnb: "Airbnb", bookingcom: "Booking.com", "booking.com": "Booking.com", agoda: "Agoda", mmt: "MakeMyTrip", makemytrip: "MakeMyTrip", expedia: "Expedia", "hotels.com": "Hotels.com", hotelscom: "Hotels.com" };
export const canonicalSource = (source) =>
  SOURCE_ALIASES[String(source || "").toLowerCase().replace(/\s+/g, "")] || source;

/* The channel badge itself. Falls back to the source's own first letter for
   an OTA not in the map above (a feed added later, or "MMT" vs "MakeMyTrip"),
   so a new channel is still identifiable rather than unlabelled. */
export function OtaMark({ source: rawSource, size = 16, title }) {
  const source = canonicalSource(rawSource);
  const cfg = SOURCE_CONFIG[source] || SOURCE_CONFIG.Manual;
  const known = !!SOURCE_CONFIG[source];
  const mark = known ? cfg.mark : String(source || "?").slice(0, 2).toUpperCase();
  // Height-driven pill, not a fixed square: a square sized for "A" squeezes
  // "MMT" and "B." down to an unreadable 5-6px, which is what made these look
  // like coloured specks. Width grows with the mark instead, and the font is
  // floored at 9px so it stays legible at every call site.
  const fontSize = Math.max(9, Math.round(size * 0.58));
  return (
    <span
      title={title || (known ? cfg.label : source)}
      aria-label={known ? cfg.label : source}
      className="inline-flex items-center justify-center rounded font-bold text-white flex-shrink-0 leading-none tracking-tight"
      style={{
        background: cfg.brand,
        height: size,
        minWidth: size,
        padding: mark.length > 1 ? `0 ${Math.round(size * 0.22)}px` : 0,
        fontSize,
      }}
    >
      {mark}
    </span>
  );
}

function datesInRange(start, end) {
  const dates = [];
  const cur = new Date(start);
  while (cur < new Date(end)) {
    dates.push(cur.toDateString());
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

/* ── One month-grid cell in Inventory or Rates mode ────────────────────
   Deliberately terse: a day cell is ~100px and shares the month with 30
   others, so this shows the number that decides the day and defers the
   per-room breakdown to the List view, which has room for it. ── */
function DayCellDetail({ mode, info, blocked = 0 }) {
  if (!info) return <p className="text-[10px] text-gray-300">—</p>;

  if (mode === "inventory") {
    const out = info.available === 0;
    return (
      <div>
        <p className={`text-[15px] font-semibold leading-none tabular-nums ${out ? "text-rose-600" : "text-gray-800"}`}>
          {info.available}
        </p>
        <p className="text-[10px] text-gray-400 mt-0.5">of {info.total} free</p>
        {/* Blocks are otherwise invisible in this view — they only lower the
            free count, which reads the same as a guest booking. */}
        {blocked > 0 && (
          <p className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 rounded px-1 mt-1">
            {blocked} blocked
          </p>
        )}
      </div>
    );
  }

  // Rates: a month cell can't carry every room's price, so lead with the
  // lowest — the figure a guest sees first — and say how many others exist.
  const rates = info.rooms.map(r => r.rate).filter(Boolean);
  if (rates.length === 0) return <p className="text-[10px] text-gray-300">—</p>;
  const low = Math.min(...rates);
  const anyOverride = info.rooms.some(r => r.overridden);
  return (
    <div>
      <p className={`text-[13px] font-semibold leading-none tabular-nums ${anyOverride ? "text-amber-600" : "text-gray-800"}`}>
        ₹{low.toLocaleString("en-IN")}
      </p>
      <p className="text-[10px] text-gray-400 mt-0.5">
        {rates.length > 1 ? `from · ${rates.length} types` : "per night"}
      </p>
    </div>
  );
}

/* ── Booking detail modal ── */
export function BookingModal({ booking, onClose }) {
  const { authHeaders } = useAuth();
  if (!booking) return null;
  const cfg = SOURCE_CONFIG[canonicalSource(booking.source)] || SOURCE_CONFIG.Manual;
  const guest = booking.guest;
  const nights = Math.ceil(
    (new Date(booking.checkOut) - new Date(booking.checkIn)) / 86400000
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/80 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className={`${cfg.light} ${cfg.border} border-b px-6 py-4 flex items-start justify-between`}>
          <div>
            <span className={`text-xs font-semibold ${cfg.text} uppercase tracking-wider`}>
              {booking.source}
            </span>
            <h2 className="text-lg font-bold text-gray-900 mt-0.5">
              {bookingLabel({ ...booking, guest })}
            </h2>
            <p className="text-sm text-gray-500">{booking.bookingRef}</p>
          </div>
          <button onClick={onClose} className="w-11 h-11 flex items-center justify-center hover:bg-white/60 rounded-lg">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Check-in</p>
              <p className="font-semibold text-gray-900">
                {new Date(booking.checkIn).toLocaleDateString("en-IN", {
                  day: "numeric", month: "short", year: "numeric"
                })}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Check-out</p>
              <p className="font-semibold text-gray-900">
                {new Date(booking.checkOut).toLocaleDateString("en-IN", {
                  day: "numeric", month: "short", year: "numeric"
                })}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center bg-gray-50 rounded-xl p-3">
            <div>
              <p className="text-xl font-bold text-gray-900">{nights}</p>
              <p className="text-xs text-gray-400">Nights</p>
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{booking.guestCount || 1}</p>
              <p className="text-xs text-gray-400">Guests</p>
            </div>
            <div>
              <p className={`text-sm font-semibold ${
                booking.status === "Confirmed" ? "text-green-600" :
                booking.status === "Completed" ? "text-blue-600" :
                booking.status === "Cancelled" ? "text-red-600" :
                booking.status === "No-Show" ? "text-orange-600" : "text-amber-600"
              }`}>{booking.status}</p>
              <p className="text-xs text-gray-400">Status</p>
            </div>
          </div>

          {guest && (
            <div className="space-y-2">
              {guest.whatsapp && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">WhatsApp</span>
                  <a href={`https://wa.me/${guest.whatsapp}`}
                    className="text-green-600 font-medium hover:underline"
                    target="_blank" rel="noopener noreferrer">
                    {guest.whatsapp}
                  </a>
                </div>
              )}
              {guest.purposeOfVisit && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Purpose</span>
                  <span className="text-gray-700">{guest.purposeOfVisit}</span>
                </div>
              )}
            </div>
          )}

          {booking.payment?.totalAmount > 0 && (
            <div className="bg-amber-50 rounded-xl p-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total</span>
                <span className="font-semibold">₹{booking.payment.totalAmount?.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Advance Paid</span>
                <span className="text-green-600 font-medium">₹{booking.payment.advancePaid?.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-amber-200 pt-1.5 mt-1.5">
                <span className="text-gray-600">Balance Due</span>
                <span className="text-rose-600 font-bold">₹{booking.payment.remainingBalance?.toLocaleString("en-IN")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex gap-2">
          <button
            onClick={() => openInvoicePreview(booking._id, authHeaders, API)}
            className="flex-1 text-center text-sm font-medium py-2.5 rounded-xl
              bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Download Invoice
          </button>
          <button
            onClick={onClose}
            className="flex-1 text-sm font-medium py-2.5 rounded-xl
              bg-gradient-to-r from-amber-500 to-orange-500 text-white
              hover:from-amber-600 hover:to-orange-600 transition-all shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Calendar ── */
export default function CalendarPage() {
  const { authHeaders } = useAuth();
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [bookings, setBookings] = useState([]);
  // What the month cells show. "bookings" is the original calendar and stays
  // the default, so this only ever adds views — Inventory and Rates answer
  // "can I sell this night, and for how much", which the booking chips never
  // could. Remembered per viewer like the month/list choice.
  const [cellMode, setCellModeRaw] = useState(() => {
    try { return localStorage.getItem("hostsync_calendar_cells") || "bookings"; } catch { return "bookings"; }
  });
  const setCellMode = (m) => {
    setCellModeRaw(m);
    try { localStorage.setItem("hostsync_calendar_cells", m); } catch {}
  };
  const [grid, setGrid] = useState(null);   // /rates-grid for the shown month
  // Blocking was previously only reachable in the list view, and only via a
  // drag — a gesture nobody discovers and which cannot fire on touch at all.
  const [showBlock, setShowBlock] = useState(false);
  // The nights whose rooms are open in the day panel (Inventory view) —
  // { date, nights }: one tapped day, or a run dragged across.
  const [dayOpen, setDayOpen] = useState(null);
  // The nights whose prices are open (Rates view), same shape.
  const [rateDayOpen, setRateDayOpen] = useState(null);
  const nightsBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000) + 1;
  const range = useRangeSelect((_, first, last) => {
    const open = { date: first, nights: nightsBetween(first, last) };
    (cellMode === "rates" ? setRateDayOpen : setDayOpen)(open);
  });
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  // Persisted per-viewer preference — a page nav or refresh used to always
  // reset this back to "month", losing whichever view the user had picked.
  const [viewMode, setViewModeRaw] = useState(() => {
    try { return localStorage.getItem("hostsync_calendar_view") || "month"; }
    catch { return "month"; }
  });
  const setViewMode = (mode) => {
    setViewModeRaw(mode);
    try { localStorage.setItem("hostsync_calendar_view", mode); } catch {}
  };
  // Tracks what's HIDDEN, not what's visible. The old shape listed the
  // visible sources and seeded it from SOURCE_CONFIG's keys, so any booking
  // with a source not in that map — "MMT" from a feed configured with a short
  // name, or any OTA added later — was silently dropped from the grid with no
  // chip, no empty state, and no way to discover it. Inverting the test means
  // the calendar can never hide a booking it simply doesn't recognise: a
  // source has to be switched off deliberately to disappear.
  const [hiddenSources, setHiddenSources] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const from = toLocalISODate(new Date(year, month, 1));
    const to   = toLocalISODate(new Date(year, month + 1, 0));
    try {
      const res  = await fetch(`${API}/calendar?from=${from}&to=${to}`, { headers: authHeaders() });
      const json = await res.json();
      setBookings(json.data || []);
    } catch {}
    setLoading(false);
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  // Rates/inventory for the displayed month, fetched only when a view that
  // needs it is active — the plain Bookings calendar makes no extra request.
  const loadGrid = useCallback(async () => {
    if (cellMode === "bookings") return;
    const first = new Date(year, month, 1);
    const days  = new Date(year, month + 1, 0).getDate();
    try {
      const res  = await fetch(`${API}/rates-grid?from=${toLocalISODate(first)}&days=${days}`,
        { headers: authHeaders() });
      const json = await res.json();
      if (json.success) setGrid(json.data);
    } catch { /* the calendar still works without it */ }
  }, [year, month, cellMode]);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  // date -> { available, total, rooms:[{name, rate, available, totalUnits}] }
  const byDate = useMemo(() => {
    const out = {};
    if (!grid) return out;
    grid.dates.forEach((ds, i) => {
      out[ds] = {
        available: grid.rooms.reduce((n, r) => n + r.cells[i].available, 0),
        total:     grid.rooms.reduce((n, r) => n + r.totalUnits, 0),
        rooms:     grid.rooms.map(r => ({
          name: r.name, rate: r.cells[i].rate,
          available: r.cells[i].available, totalUnits: r.totalUnits,
          overridden: r.cells[i].rate !== r.basePrice,
        })),
      };
    });
    return out;
  }, [grid]);

  const blockedByDate = useMemo(() => {
    const out = {};
    bookings.filter(b => b.status === "Blocked").forEach(b => {
      datesInRange(b.checkIn, b.checkOut).forEach(ds => {
        const key = toLocalISODate(new Date(ds));
        out[key] = (out[key] || 0) + 1;
      });
    });
    return out;
  }, [bookings]);

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  }

  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel  = new Date(year, month).toLocaleString("en-IN", {
    month: "long", year: "numeric"
  });

  // Build day → bookings map
  const dayMap = {};
  bookings
    .filter(b => !hiddenSources.includes(canonicalSource(b.source)))
    .forEach(b => {
      datesInRange(b.checkIn, b.checkOut).forEach(ds => {
        if (!dayMap[ds]) dayMap[ds] = [];
        dayMap[ds].push(b);
      });
    });

  function toggleSource(src) {
    setHiddenSources(prev =>
      prev.includes(src) ? prev.filter(s => s !== src) : [...prev, src]
    );
  }

  // The OTA tags are always shown, plus whatever else the data contains, so
  // an unrecognised source still gets a chip instead of being invisible.
  const legendSources = [...new Set([
    ...Object.keys(SOURCE_CONFIG),
    ...bookings.map(b => canonicalSource(b.source)).filter(Boolean),
  ])];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Availability Calendar</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {bookings.length} booking{bookings.length !== 1 ? "s" : ""} this month
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode("month")}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 min-h-[44px] sm:min-h-0
                rounded-lg text-xs font-medium transition-colors
                ${viewMode === "month" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              <Grid3x3 size={13} /> Month
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 min-h-[44px] sm:min-h-0
                rounded-lg text-xs font-medium transition-colors
                ${viewMode === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              <Rows3 size={13} /> List
            </button>
          </div>
          <button
            onClick={load}
            className="tap-44 flex items-center gap-2 text-sm font-medium text-gray-600
              hover:text-gray-900 bg-white border border-gray-200 px-3 py-2
              rounded-xl shadow-sm hover:shadow transition-all"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={() => setShowBlock(true)}
            className="tap-44 flex items-center gap-2 text-sm font-medium text-gray-600
              hover:text-gray-900 bg-white border border-gray-200 px-3 py-2
              rounded-xl shadow-sm hover:shadow transition-all"
          >
            <CalendarOff size={14} />
            Block dates
          </button>
        </div>
      </div>

      {/* Month view only. The list view is a room timeline that now carries
          availability, rate and bookings on one screen, so switching between
          them there would hide data rather than reveal it — tabs are only
          needed where a day cell is too small to hold all three. */}
      {viewMode === "month" && (
      <div className="flex gap-1 border-b border-gray-200 mb-5 -mx-1 px-1 overflow-x-auto">
        {[["bookings", "Bookings"], ["inventory", "Inventory"], ["rates", "Rates"]].map(([id, label]) => (
          <button key={id} onClick={() => setCellMode(id)}
            className={`px-4 py-2.5 min-h-[44px] text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              cellMode === id ? "border-amber-500 text-amber-600" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}>
            {label}
          </button>
        ))}
      </div>
      )}
      {viewMode === "month" && cellMode === "inventory" && (
        <p className="-mt-3 mb-4 text-xs text-gray-500">Tap a day to block or unblock rooms for that night — or drag across days to pick several (on a phone, press and hold, then slide).</p>
      )}
      {viewMode === "month" && cellMode === "rates" && (
        <p className="-mt-3 mb-4 text-xs text-gray-500">Tap a day to set a special rate for that night — or drag across days to price several (on a phone, press and hold, then slide).</p>
      )}

      {showBlock && (
        <BlockDatesModal
          authHeaders={authHeaders}
          onClose={() => setShowBlock(false)}
          onSaved={() => { load(); loadGrid(); }}   /* refetch; ignores the returned array */
        />
      )}

      {rateDayOpen && (
        <DayRatesSheet
          key={`${rateDayOpen.date}-${rateDayOpen.nights}`}
          date={rateDayOpen.date}
          initialNights={rateDayOpen.nights}
          authHeaders={authHeaders}
          onClose={() => setRateDayOpen(null)}
          onChanged={() => loadGrid()}
        />
      )}

      {dayOpen && (
        <DayInventorySheet
          key={`${dayOpen.date}-${dayOpen.nights}`}
          date={dayOpen.date}
          initialNights={dayOpen.nights}
          authHeaders={authHeaders}
          onClose={() => setDayOpen(null)}
          onChanged={() => Promise.all([load(), loadGrid()])}
        />
      )}

      {viewMode === "list" ? (
        <CalendarListView authHeaders={authHeaders} />
      ) : (
      <>
      {/* Source filter legend */}
      <div className="flex flex-wrap gap-2 mb-5">
        {legendSources.map(src => {
          const cfg     = SOURCE_CONFIG[src] || SOURCE_CONFIG.Manual;
          const visible = !hiddenSources.includes(src);
          return (
            <button
              key={src}
              onClick={() => toggleSource(src)}
              className={`flex items-center gap-2 px-3 py-1.5 min-h-[44px] sm:min-h-0
                rounded-full text-xs font-medium border transition-all duration-150
                ${visible
                  ? `${cfg.light} ${cfg.text} ${cfg.border}`
                  : "bg-gray-50 text-gray-400 border-gray-100"
                }`}
            >
              {visible
                ? <OtaMark source={src} size={20} />
                : <span className="w-3.5 h-3.5 rounded bg-gray-300 flex-shrink-0" />}
              {SOURCE_CONFIG[src] ? cfg.label : src}
            </button>
          );
        })}
      </div>

      {/* Calendar grid */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Month nav */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <button onClick={prevMonth} className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <ChevronLeft size={18} className="text-gray-500" />
          </button>
          <h2 className="text-sm font-semibold text-gray-900">{monthLabel}</h2>
          <button onClick={nextMonth} className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <ChevronRight size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Day labels + cells — horizontally scrollable on narrow screens
            so a 7-column month grid stays readable instead of squashing */}
        <div className="overflow-x-auto">
        <div className="min-w-[640px]">
        <div className="grid grid-cols-7 border-b border-gray-100">
          {["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"].map(d => (
            <div key={d} className="py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
              {d.slice(0, 3)}
            </div>
          ))}
        </div>

        {/* Day cells */}
        {/* data-no-pull: a press-and-drag down the weeks must select days,
            not trigger the app's pull-to-refresh. */}
        <div className="grid grid-cols-7 divide-x divide-y divide-gray-100"
          {...(cellMode !== "bookings" ? { "data-no-pull": "" } : {})}>
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`e${i}`} className="min-h-[100px] bg-gray-50/50" />
          ))}

          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateKey = new Date(year, month, day).toDateString();
            const dayBookings = dayMap[dateKey] || [];
            const isToday =
              day === today.getDate() &&
              month === today.getMonth() &&
              year === today.getFullYear();

            const cellDate = toLocalISODate(new Date(year, month, day));
            // Inventory opens the block panel, Rates the price panel; the
            // Bookings view keeps its chips and is not a tap target itself.
            const tappable = cellMode === "inventory" || cellMode === "rates";
            const openDay = () => (cellMode === "rates" ? setRateDayOpen : setDayOpen)({ date: cellDate, nights: 1 });
            const inDrag = tappable && range.isSelected(cellMode, cellDate);
            const dayName = new Date(year, month, day).toLocaleDateString("en-IN", { day: "numeric", month: "long" });
            return (
              <div
                key={day}
                {...(tappable ? {
                  role: "button", tabIndex: 0,
                  "aria-label": cellMode === "rates" ? `Prices on ${dayName} — change the rate` : `Rooms on ${dayName} — block or unblock`,
                  onClick: openDay,
                  onKeyDown: e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDay(); } },
                  ...range.bind(cellMode, cellDate),
                } : {})}
                className={`min-h-[100px] p-2 transition-colors
                  ${inDrag ? "bg-amber-100 ring-2 ring-inset ring-amber-400" : isToday ? "bg-amber-50" : "hover:bg-gray-50"}
                  ${tappable ? "cursor-pointer select-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-400" : ""}`}
                style={tappable ? { WebkitTouchCallout: "none" } : undefined}
              >
                {/* Date number */}
                <div className={`w-7 h-7 flex items-center justify-center rounded-full mb-1
                  text-[13px] font-semibold tabular-nums transition-colors
                  ${isToday
                    ? "bg-amber-500 text-white"
                    : "text-gray-700"
                  }`}
                >
                  {day}
                </div>

                {/* Inventory / Rates replace the booking chips for this cell.
                    Rendered from the same /rates-grid data the Rates page
                    uses, so the two screens can never disagree. */}
                {cellMode !== "bookings" ? (
                  <DayCellDetail mode={cellMode} info={byDate[cellDate]} blocked={blockedByDate[cellDate] || 0} />
                ) : (
                <div className="space-y-0.5">
                  {dayBookings.slice(0, 3).map((b, idx) => {
                    const cfg = SOURCE_CONFIG[canonicalSource(b.source)] || SOURCE_CONFIG.Manual;
                    const gName = bookingLabel(b);
                    return (
                      // The one deliberate exception to CLAUDE.md I7's 44px
                      // minimum: these are density marks inside a day cell, and
                      // a 44px bar would mean one booking per cell and a grid
                      // taller than the phone. The List view above is the
                      // touch-sized way to read the same data, and it now
                      // persists once chosen — so the 44px path exists, it just
                      // isn't the month grid.
                      <button
                        key={`${b._id}-${idx}`}
                        onClick={() => setSelected(b)}
                        className={`w-full flex items-center gap-1.5 text-left px-1 py-1 rounded text-[10.5px] font-medium
                          ${cfg.light} ${cfg.text} hover:opacity-80 transition-opacity`}
                      >
                        <OtaMark source={b.source} size={16} />
                        <span className="truncate">{gName}</span>
                      </button>
                    );
                  })}
                  {dayBookings.length > 3 && (
                    <p className="text-[10px] text-gray-400 pl-1">
                      +{dayBookings.length - 3} more
                    </p>
                  )}
                </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
        </div>
      </div>
      </>
      )}

      {/* Booking detail modal */}
      <BookingModal booking={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
